import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CameraIcon, ImageIcon, UploadCloudIcon, XIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { allPoints, formatPhotoTimestamp, getStats, pointReference } from '../../utils/quality';
interface PhotosTabProps {
  report: QualityReport;
  onAssignPhoto: (photoId: string, pointId: string | undefined) => void;
}
type Filter = 'all' | 'unassigned' | 'no-description';
export function PhotosTab({
  report,
  onAssignPhoto
}: PhotosTabProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [openPhotoId, setOpenPhotoId] = useState<string | null>(null);
  const stats = getStats(report);
  const points = allPoints(report);
  const noDescription = report.photos.filter(p => !p.description).length;
  const visible = useMemo(() => {
    if (filter === 'unassigned') return report.photos.filter(p => !p.controlPointId);
    if (filter === 'no-description') return report.photos.filter(p => !p.description);
    return report.photos;
  }, [report.photos, filter]);
  const filters: {
    id: Filter;
    label: string;
    count?: number;
  }[] = [{
    id: 'all',
    label: 'Alle'
  }, {
    id: 'unassigned',
    label: 'Ikke tildelt',
    count: stats.unassignedPhotos
  }, {
    id: 'no-description',
    label: 'Mangler beskrivelse',
    count: noDescription
  }];
  return <div className="space-y-5" data-id="element-743" data-mp="3oxse409g26ea">
      {/* Upload */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-744" data-mp="3ozd7vnnxplb3">
        <div className="flex flex-wrap items-center gap-4" data-id="element-745" data-mp="3txofrsuo5lqo">
          <div className="flex min-w-0 flex-1 items-start gap-3" data-id="element-746" data-mp="2qc05i9fpgcv">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600" data-id="element-747" data-mp="2p0wrjjwiama">
              <UploadCloudIcon className="h-5 w-5" data-id="element-748" data-mp="1e5hgvq6ehffh" />
            </span>
            <div className="min-w-0" data-id="element-749" data-mp="1e57pby5pj07o">
              <h3 className="text-base font-semibold text-stone-900" data-id="element-750" data-mp="1i6syb8f4eucl">
                Træk billeder hertil for at uploade
              </h3>
              <p className="mt-0.5 text-sm text-stone-500" data-id="element-751" data-mp="3f6durkbdypmb">
                Tag billeder mens du arbejder. Du behøver ikke vælge
                kontrolpunkt nu — det kobler vi bagefter.
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2" data-id="element-752" data-mp="3o60yicrl87ij">
            <button className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50" data-id="element-753" data-mp="3jalo5ussgtqv">
              <ImageIcon className="h-4 w-4" data-id="element-754" data-mp="2rj5szcd61yp1" />
              Vælg fra galleri
            </button>
            <button className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-755" data-mp="35akmwe8m3kav">
              <CameraIcon className="h-4 w-4" data-id="element-756" data-mp="2csyknpgvhw22" />
              Tag foto
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-id="element-757" data-mp="27tluauqsm6uq">
        <span className="mr-1 text-sm font-semibold text-stone-900" data-id="element-758" data-mp="nbkx8en823tg">
          {report.photos.length} fotos
        </span>
        {filters.map(f => {
        const isActive = filter === f.id;
        return <button key={f.id} onClick={() => setFilter(f.id)} className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-stone-900 text-white' : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50'}`} data-id="element-759" data-mp="echdd8g8dkdw">
              {f.label}
              {f.count !== undefined && f.count > 0 && <span className={isActive ? 'text-white/60' : 'text-stone-400'} data-id="element-760" data-mp="1210gl9kfb6z4">
                  {' '}
                  · {f.count}
                </span>}
            </button>;
      })}
        <p className="ml-auto text-xs text-stone-400" data-id="element-761" data-mp="3mqy75b5ijs1f">
          Klik et foto for at tildele kontrolpunkt
        </p>
      </div>

      {/* Grid */}
      {visible.length === 0 ? <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-12 text-center" data-id="element-762" data-mp="14s5kh9pxs7v">
          <p className="text-sm text-stone-500" data-id="element-763" data-mp="3t8b6ujdkk3on">
            Ingen fotos matcher dette filter.
          </p>
        </div> : <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-764" data-mp="3oaj7536ond9w">
          {visible.map(photo => {
        const ref = photo.controlPointId ? pointReference(report, photo.controlPointId) : null;
        const point = points.find(p => p.id === photo.controlPointId);
        const isOpen = openPhotoId === photo.id;
        return <li key={photo.id} className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-765" data-mp="atigwjqnesjj">
                <button onClick={() => setOpenPhotoId(isOpen ? null : photo.id)} className="block w-full text-left" data-id="element-766" data-mp="2vksnyfq71l6x">
                  <span className="block aspect-[4/3] overflow-hidden bg-stone-100" data-id="element-767" data-mp="2lip0yuk0fyuy">
                    <img src={photo.url} alt={photo.description || 'Dokumentationsfoto'} className="h-full w-full object-cover" data-id="element-768" data-mp="2qggrbk50bcx1" />
                  </span>
                </button>

                <div className="px-3.5 py-3" data-id="element-769" data-mp="347vbhuanhk5o">
                  <p className="text-xs text-stone-400" data-id="element-770" data-mp="3473rzhubvcvr">
                    {formatPhotoTimestamp(photo.takenAt)}
                  </p>
                  {point ? <p className="mt-1 truncate text-sm font-medium text-stone-900" data-id="element-771" data-mp="3e6ku5padf8mz">
                      <span className="text-brand-700" data-id="element-772" data-mp="3j4w4e14kvl5q">{ref}:</span>{' '}
                      {point.title}
                    </p> : <p className="mt-1 text-sm font-medium text-amber-600" data-id="element-773" data-mp="1jtlbfnap1ybh">
                      Ikke tildelt
                    </p>}

                  {isOpen ? <div className="mt-3 border-t border-stone-100 pt-3" data-id="element-774" data-mp="1gq5mxb4cyebv">
                      <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-500" data-id="element-775" data-mp="1bs45eumkyn2i">
                        Tildel kontrolpunkt
                      </label>
                      <select value={photo.controlPointId ?? ''} onChange={e => onAssignPhoto(photo.id, e.target.value || undefined)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-776" data-mp="1lnysq31r16bm">
                        <option value="" data-id="element-777" data-mp="24g9o7bu2q1zp">Ikke tildelt</option>
                        {points.map((p, i) => <option key={p.id} value={p.id} data-id="element-778" data-mp="2icwonol8jaad" data-mpi={i}>
                            KP{i + 1}: {p.title}
                          </option>)}
                      </select>
                      <button onClick={() => setOpenPhotoId(null)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-779" data-mp="2x054j2pho3so">
                        <XIcon className="h-3.5 w-3.5" data-id="element-780" data-mp="2sz1n4brslzpc" />
                        Luk
                      </button>
                    </div> : <motion.button whileTap={{
              scale: 0.97
            }} onClick={() => setOpenPhotoId(photo.id)} className="mt-2 text-xs font-medium text-stone-500 transition-colors hover:text-brand-700" data-id="element-781" data-mp="2j2qatls7d9uw">
                      {point ? 'Skift kontrolpunkt' : 'Tilføj kontrolpunkt'}
                    </motion.button>}
                </div>
              </li>;
      })}
        </ul>}

      {/* Footer stats */}
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-stone-200 bg-stone-200 sm:grid-cols-4" data-id="element-782" data-mp="2fsnpc25bkbiv">
        {[{
        label: 'Kontrolpunkter',
        value: stats.totalPoints
      }, {
        label: 'Dokumenteret',
        value: stats.documentedPoints
      }, {
        label: 'Afvigelser',
        value: stats.deviations
      }, {
        label: 'Mangler foto',
        value: stats.missingPhoto
      }].map(item => <div key={item.label} className="bg-white px-4 py-3.5" data-id="element-783" data-mp="1ww5omv8ht1ah">
            <dt className="text-xs font-medium uppercase tracking-wide text-stone-500" data-id="element-784" data-mp="1s3w5by1vuzh6">
              {item.label}
            </dt>
            <dd className="mt-0.5 text-xl font-bold text-stone-900" data-id="element-785" data-mp="1n5b1zprc3zoi">
              {item.value}
            </dd>
          </div>)}
      </dl>
    </div>;
}